import cryptoHandler from './cryptoHandler';
import { InvalidAuthTokenError } from '@/domain/errors/authErrors.js';

const PUBLIC_AUTH_BYPASS_PATHS = ['/oauth/token', '/register'];

/**
 * Manages storage and retrieval of encrypted tokens using localStorage.
 */
const authTokenHandler = {
  /**
   * Encrypts and stores the token data securely in localStorage.
   * @param {Object} token_data - The token data to store
   *  (includes access_token, refresh_token, and expires_in).
   * @param {CryptoKey} key - The cryptographic key used for encryption.
   * @returns {Promise<void>} A promise that resolves when the operation is
   *  complete.
   */
  storeToken: function (token_data) {
    const tokenString = JSON.stringify(token_data);
    return cryptoHandler
      .generateKey()
      .then((key) => cryptoHandler.encryptToken(tokenString, key))
      .then(({ encrypted, iv, key }) => {
        localStorage.setItem(
          'encryptedTokenData',
          JSON.stringify({ encrypted, iv })
        );
        return cryptoHandler.exportKey(key);
      })
      .then((exportedKey) =>
        localStorage.setItem('encryptionKey', exportedKey)
      );
  },

  /**
   * Retrieves, decrypts, and returns the token data stored in localStorage.
   * @returns {Promise<Object>} A promise that resolves with the decrypted
   * token data.
   */
  retrieveToken: function () {
    const stored = localStorage.getItem('encryptedTokenData');
    if (!stored) {
      return Promise.reject(new InvalidAuthTokenError('No token stored'));
    }
    const { encrypted, iv } = JSON.parse(stored);
    const exportedKey = localStorage.getItem('encryptionKey');
    if (!encrypted || !iv || !exportedKey) {
      return Promise.reject(new InvalidAuthTokenError('Incomplete token data'));
    }

    return cryptoHandler
      .importKey(exportedKey)
      .then((key) => cryptoHandler.decryptToken(encrypted, key, iv))
      .then((decryptedTokenString) => JSON.parse(decryptedTokenString));
  },

  /**
   * Clears all locally persisted auth artifacts for both the current and
   * legacy storage layouts.
   *
   * Logout should leave no encrypted token payload behind, even if the client
   * still has storage from an earlier implementation that used different keys.
   */
  clearStoredToken() {
    localStorage.removeItem('encryptedTokenData');
    localStorage.removeItem('encryptionKey');

    // Legacy keys are removed as a defensive cleanup path during logout.
    localStorage.removeItem('encryptedToken');
    localStorage.removeItem('iv');
  },

  /**
   * Identifies requests that are intentionally public and must not depend on
   * any previously stored auth token.
   *
   * Keeping this rule centralized prevents request/response interceptors from
   * drifting and accidentally re-blocking public auth entry points.
   *
   * @param {string | undefined} requestUrl - The Axios request URL.
   * @returns {boolean} `true` when token handling should be skipped.
   */
  shouldBypassTokenHandling(requestUrl) {
    if (!requestUrl) {
      return false;
    }

    return PUBLIC_AUTH_BYPASS_PATHS.some((path) => requestUrl.includes(path));
  },

  /**
   * Checks if a token is still valid.
   * @returns {boolean} true if the token is valid, false otherwise.
   */
  isTokenValid(token_data) {
    if (
      !token_data ||
      !token_data.expires_at ||
      !token_data.refresh_token ||
      !token_data.access_token
    ) {
      return false;
    }
    const now = Math.floor(Date.now() / 1000); // Get current time in seconds
    return now < token_data.expires_at;
  }
};
export default authTokenHandler;
